import React, { useState } from "react";
import { Calendar, Check } from "lucide-react";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useCms } from "../context/CmsContext";

const initial = {
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    situation: "lyceen",
    parcoursup: false,
    message: "",
    contact_pref: "email",
};
export default function Contact() {
    const cms = useCms();
    const t = (k, fb) => cms.get(k, fb);
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.prenom || !form.nom || !form.email || !form.message) {
            toast.error("Merci de compléter les champs obligatoires.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contacts", form);
            setDone(true);
            setForm(initial);
            toast.success("Message envoyé. Nous vous répondons sous 48h.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'envoi");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact" className="bg-ink-800 min-h-screen text-white">
            <PublicNav />
            <Toaster position="top-center" theme="dark" richColors />

            <section className="pt-20 pb-[42px] lg:pt-24 lg:pb-[42px] px-6 md:px-12">
                <div className="max-w-5xl mx-auto">
                    <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">Contact</span>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[1.1]">
                        Nous <span className="text-ice">contacter</span>
                    </h1>
                </div>
            </section>

            <section className="px-6 md:px-12 pb-[42px] lg:pb-[42px]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
                    {/* Info cards */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-ink-700 rounded-xl border border-ice/15 p-7">
                            <div className="flex items-center gap-3 text-ice">
                                <Calendar size={18} />
                                <span className="font-serif text-xl">Premier rendez-vous</span>
                            </div>
                            <p className="mt-4 text-white/85 leading-relaxed whitespace-pre-line">
                                {t("contact.rdv_text", "Gratuit — sans engagement — 30 minutes pour faire connaissance et voir comment nous pouvons vous aider.")}
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3">
                        {done ? (
                            <div
                                data-testid="contact-success"
                                className="bg-ink-700 rounded-xl border border-ice/30 p-10 text-center"
                            >
                                <div className="w-14 h-14 rounded-full bg-ice/15 flex items-center justify-center mx-auto">
                                    <Check className="text-ice" size={26} />
                                </div>
                                <h3 className="font-serif text-3xl mt-5">Message bien reçu</h3>
                                <p className="mt-3 text-white/75 max-w-md mx-auto">
                                    Nous reviendrons vers vous sous 48h, même le week-end.
                                </p>
                                <button
                                    data-testid="contact-send-another"
                                    className="mt-6 text-sm text-ice hover:text-white transition-colors underline underline-offset-4"
                                    onClick={() => setDone(false)}
                                >
                                    Envoyer un autre message
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid="contact-form"
                                onSubmit={submit}
                                className="bg-ink-700 rounded-xl border border-ice/15 p-7 md:p-10 space-y-5"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Field label="Prénom *" testId="contact-prenom">
                                        <input
                                            value={form.prenom}
                                            onChange={(e) => set("prenom", e.target.value)}
                                            data-testid="contact-input-prenom"
                                            className={inputClass}
                                            required
                                        />
                                    </Field>
                                    <Field label="Nom *" testId="contact-nom">
                                        <input
                                            value={form.nom}
                                            onChange={(e) => set("nom", e.target.value)}
                                            data-testid="contact-input-nom"
                                            className={inputClass}
                                            required
                                        />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Field label="Email *">
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => set("email", e.target.value)}
                                            data-testid="contact-input-email"
                                            className={inputClass}
                                            required
                                        />
                                    </Field>
                                    <Field label="Votre situation">
                                        <select
                                            value={form.situation}
                                            onChange={(e) => set("situation", e.target.value)}
                                            data-testid="contact-select-situation"
                                            className={inputClass}
                                        >
                                            <option value="collegien">Collégien.ne</option>
                                            <option value="lyceen">Lycéen.ne (2de / 1re / Tle)</option>
                                            <option value="etudiant">Étudiant.e souhaitant changer de voie</option>
                                            <option value="parent">Parent</option>
                                        </select>
                                    </Field>
                                </div>

                                <label
                                    data-testid="contact-toggle-parcoursup"
                                    className={`flex items-center gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
                                        form.parcoursup
                                            ? "bg-ice text-white border-ice"
                                            : "bg-ink-800 border-white/10 hover:border-ice/40"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={form.parcoursup}
                                        onChange={(e) => set("parcoursup", e.target.checked)}
                                        className="sr-only"
                                    />
                                    <span
                                        className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                                            form.parcoursup ? "bg-white border-white" : "border-white/40"
                                        }`}
                                    >
                                        {form.parcoursup && <Check size={12} className="text-ice" />}
                                    </span>
                                    <span className="text-base">Je suis concerné.e par Parcoursup</span>
                                </label>

                                <Field label="Votre message *">
                                    <textarea
                                        value={form.message}
                                        onChange={(e) => set("message", e.target.value)}
                                        rows={5}
                                        data-testid="contact-input-message"
                                        className={inputClass + " resize-none"}
                                        required
                                    />
                                </Field>

                                <Field label="Comment préférez-vous être contacté ? *">
                                    <select
                                        value={form.contact_pref}
                                        onChange={(e) => set("contact_pref", e.target.value)}
                                        data-testid="contact-select-pref"
                                        className={inputClass}
                                        required
                                    >
                                        <option value="email">Par email</option>
                                        <option value="telephone">Par téléphone</option>
                                    </select>
                                </Field>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit-button"
                                    className="w-full bg-ice text-ink-900 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-7 py-4 rounded-md font-medium tracking-wide"
                                >
                                    {submitting ? "Envoi en cours…" : "Envoyer"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const inputClass =
    "w-full bg-ink-800 border border-white/15 text-white placeholder-white/40 focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-4 py-3 outline-none transition-colors text-sm";

function Field({ label, children, testId }) {
    return (
        <label data-testid={testId} className="block">
            <span className="font-serif text-base text-ice">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
